import {
  Button,
  Chip,
  Label,
  Spinner,
  TextArea,
  TextField,
} from "@heroui/react";
import { useState } from "react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { AccountFlag, AccountFlagCode } from "@/src/libs/moderation.type";
import { ACCOUNT_FLAGS, FLAG_KEY, FLAG_TONE } from "@/src/libs/moderation.type";
import { useAccountFlags } from "@/src/pages/AccountDetail/hooks/useAccountFlags";

/**
 * Les étiquettes internes d'un compte.
 *
 * ## Ce qu'elles sont
 *
 * Des notes que la modération se passe, **jamais** une sanction : un drapeau
 * n'ouvre ni ne ferme aucun droit. C'est ce qui permet de marquer un dossier
 * sans avoir à trancher — et c'est pourquoi l'écran le dit en toutes lettres :
 * sans cette phrase, on hésiterait à poser « problématique » de peur de fermer
 * un compte.
 *
 * ## L'intéressé ne les voit jamais
 *
 * Aucune route utilisateur ne lit cette table, aucune notification n'est émise.
 * Le dire ici n'est pas une précaution rhétorique : c'est ce qui décide de la
 * façon dont on rédige la note, pour un collègue et non pour son destinataire.
 *
 * ## Les huit pastilles sont toujours affichées
 *
 * Posées ou non. Une grille qui ne montrerait que les drapeaux existants
 * obligerait à ouvrir un menu pour en poser un, et surtout ne dirait pas ce qui
 * est *possible* — or c'est le vocabulaire commun de l'équipe.
 */
export function AccountFlags({
  accountId,
  flags,
}: {
  accountId: number;
  flags: AccountFlag[];
}) {
  const t = useTranslations("moderation");
  const { locale } = useLocale();
  const { setFlag, removeFlag, pendingFlag } = useAccountFlags(accountId);

  // Le drapeau dont on est en train d'écrire la note. Un seul à la fois : deux
  // champs ouverts feraient hésiter sur celui qu'on enregistre.
  const [editing, setEditing] = useState<AccountFlagCode>();
  const [note, setNote] = useState("");

  const posesParCode = new Map(flags.map((flag) => [flag.flag, flag]));

  const ouvrir = (code: AccountFlagCode) => {
    setEditing(code);
    // Le champ s'ouvre sur la note existante : réécrire « à surveiller » ne
    // doit pas commencer par retaper ce qu'on avait déjà écrit.
    setNote(posesParCode.get(code)?.note ?? "");
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-pretty text-muted">{t("flags-note")}</p>

      <ul className="flex flex-wrap gap-2">
        {ACCOUNT_FLAGS.map((code) => {
          const pose = posesParCode.get(code);
          const isPending = pendingFlag === code;

          return (
            <li key={code}>
              {/*
                ⚠️ Un `Button`, et non un `Chip` : la pastille se clique, et un
                `Chip` n'est ni focusable ni annoncé comme actionnable.
                `aria-pressed` dit l'état — la couleur seule ne s'annonce pas.
              */}
              <Button
                aria-pressed={pose !== undefined}
                className={
                  pose === undefined
                    ? "text-muted"
                    : FLAG_TONE[code] === "danger"
                      ? "text-danger"
                      : FLAG_TONE[code] === "warning"
                        ? "text-warning"
                        : "text-success"
                }
                isDisabled={pendingFlag !== undefined}
                size="sm"
                variant={pose === undefined ? "ghost" : "secondary"}
                onPress={() =>
                  pose === undefined ? ouvrir(code) : removeFlag(code)
                }
              >
                {isPending ? <Spinner color="current" size="sm" /> : null}
                {t(FLAG_KEY[code])}
              </Button>
            </li>
          );
        })}
      </ul>

      {editing ? (
        <div className="flex flex-col gap-2 rounded-lg bg-surface p-3">
          <TextField name="note" value={note} onChange={setNote}>
            <Label>
              {t("flag-note-label", { flag: t(FLAG_KEY[editing]) })}
            </Label>
            <TextArea fullWidth rows={2} />
          </TextField>

          <p className="text-xs text-pretty text-muted">{t("reason-hint")}</p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onPress={() => {
                setEditing(undefined);
                setNote("");
              }}
            >
              {t("cancel")}
            </Button>

            <Button
              size="sm"
              variant="primary"
              onPress={() => {
                setFlag(editing, note);
                setEditing(undefined);
                setNote("");
              }}
            >
              {t("flag-save")}
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        Les drapeaux posés, en clair : la pastille dit *quoi*, cette liste dit
        *pourquoi*, *par qui* et *depuis quand*. Sans elle, une note écrite il y
        a six mois par quelqu'un d'autre serait invisible.
      */}
      {flags.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {flags.map((flag) => (
            <li
              className="flex flex-col gap-1 rounded-lg bg-surface p-3"
              key={flag.flag}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Chip color={FLAG_TONE[flag.flag]} size="sm">
                  {t(FLAG_KEY[flag.flag])}
                </Chip>

                <span className="text-xs text-muted">
                  {new Intl.DateTimeFormat(locale, {
                    dateStyle: "medium",
                  }).format(new Date(flag.createdAt))}
                </span>

                <AccountBadge account={flag.createdBy} isLinked />

                <Button
                  className="ml-auto"
                  isDisabled={pendingFlag !== undefined}
                  size="sm"
                  variant="ghost"
                  onPress={() => ouvrir(flag.flag)}
                >
                  {t("flag-edit")}
                </Button>
              </div>

              {flag.note ? (
                <p className="text-sm text-pretty whitespace-pre-wrap">
                  {flag.note}
                </p>
              ) : (
                <p className="text-sm text-muted italic">{t("no-note")}</p>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
